import { Modal } from "bootstrap"
import { readStripeAppearance, observeStripeTheme } from "./stripe_appearance"

// Modale de paiement de l'espace souscripteur (« Régler », « Ajouter un moyen de paiement ») :
// seul le formulaire vient de Stripe (Payment Element, aux couleurs de l'app via
// readStripeAppearance) ; en-tête, résumé, bouton, erreurs et confirmation sont les nôtres.
//
//   const modal = openStripeElementModal({ title, closeLabel, submitLabel, onHidden })
//   modal.mount({ stripe, elementsOptions, summary: { label, amount } })
//   modal.onSubmit(async () => …)   // bouton désactivé et spinner pendant l'appel
//   modal.showSuccess(label) / modal.showError(message) / modal.hide()

export const spinnerHtml = (extraClass = "") =>
  `<span class="spinner-border spinner-border-sm spinner-border-thin ${extraClass}" role="status" aria-hidden="true"></span>`

// Même silhouette que le Payment Element : résumé, onglets carte / SEPA, deux champs, bouton.
function skeletonHtml(withSummary) {
  const bar = (style, extra = "") => `<div class="is-loading ${extra}" style="${style}"></div>`
  return `
    <div class="stripe-element-modal__skeleton" aria-hidden="true">
      ${withSummary ? `
        <div class="stripe-element-modal__summary">
          ${bar("height: 0.75rem; width: 45%;", "mb-2")}
          ${bar("height: 1.75rem; width: 30%;")}
        </div>` : ""}
      <div class="d-flex gap-2 mb-3">
        ${bar("height: 3.5rem; flex: 1;")}
        ${bar("height: 3.5rem; flex: 1;")}
      </div>
      ${bar("height: 0.7rem; width: 25%;", "mb-2")}
      ${bar("height: 2.6rem;", "mb-3")}
      <div class="d-flex gap-2 mb-4">
        ${bar("height: 2.6rem; flex: 1;")}
        ${bar("height: 2.6rem; flex: 1;")}
      </div>
      ${bar("height: 2.6rem;")}
    </div>`
}

export function openStripeElementModal({ title, closeLabel, submitLabel, withSummary = true, onHidden }) {
  const element = document.createElement("div")
  element.className = "modal fade stripe-element-modal"
  element.tabIndex = -1
  element.setAttribute("aria-hidden", "true")
  element.innerHTML = `
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"></h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div data-stripe-modal-loading>${skeletonHtml(withSummary)}</div>
          <form data-stripe-modal-form class="d-none" novalidate>
            <div class="stripe-element-modal__summary d-none" data-stripe-modal-summary>
              <div class="stripe-element-modal__summary-label" data-stripe-modal-summary-label></div>
              <div class="stripe-element-modal__summary-amount" data-stripe-modal-summary-amount></div>
            </div>
            <div data-stripe-modal-element></div>
            <div class="alert alert-danger small mt-3 mb-0 d-none" role="alert" data-stripe-modal-error></div>
            <button type="submit" class="btn btn-primary w-100 mt-3" data-stripe-modal-submit disabled></button>
          </form>
          <div class="stripe-element-modal__done d-none" data-stripe-modal-done>
            <i class="fa-solid fa-circle-check stripe-element-modal__done-icon" aria-hidden="true"></i>
            <div data-stripe-modal-done-label></div>
          </div>
        </div>
      </div>
    </div>`
  element.querySelector(".modal-title").textContent = title
  element.querySelector(".btn-close").setAttribute("aria-label", closeLabel)

  const $ = (name) => element.querySelector(`[data-stripe-modal-${name}]`)
  const submit = $("submit")
  submit.textContent = submitLabel

  document.body.appendChild(element)
  const modal = new Modal(element)
  let busy = false
  let paymentElement = null
  let unobserveTheme = null

  // Pas de fermeture pendant la confirmation (3DS, débit en cours).
  element.addEventListener("hide.bs.modal", (event) => { if (busy) event.preventDefault() })
  element.addEventListener("hidden.bs.modal", () => {
    try { paymentElement?.destroy() } catch { /* déjà détruit */ }
    unobserveTheme?.()
    modal.dispose()
    element.remove()
    onHidden?.()
  }, { once: true })
  modal.show()

  const showError = (message) => {
    const error = $("error")
    error.textContent = message || ""
    error.classList.toggle("d-none", !message)
  }

  const setBusy = (value) => {
    busy = value
    submit.disabled = value
    submit.innerHTML = value ? `${spinnerHtml("me-2")}<span></span>` : ""
    if (value) submit.lastElementChild.textContent = submitLabel
    else submit.textContent = submitLabel
    element.querySelector(".btn-close").disabled = value
  }

  return {
    element,

    // Monte le Payment Element et révèle le formulaire à la place du squelette une fois prêt.
    mount({ stripe, elementsOptions, paymentElementOptions = {}, summary }) {
      const elements = stripe.elements({ ...elementsOptions, appearance: readStripeAppearance() })
      unobserveTheme = observeStripeTheme(() => elements.update({ appearance: readStripeAppearance() }))

      if (summary) {
        $("summary").classList.remove("d-none")
        $("summary-label").textContent = summary.label || ""
        $("summary-amount").textContent = summary.amount || ""
      }

      paymentElement = elements.create("payment", { layout: "tabs", ...paymentElementOptions })
      paymentElement.on("ready", () => {
        $("loading").remove()
        $("form").classList.remove("d-none")
        submit.disabled = false
      })
      paymentElement.on("change", ({ error }) => showError(error?.message))
      paymentElement.on("loaderror", ({ error }) => this.showError(error?.message))
      paymentElement.mount($("element"))
      return elements
    },

    onSubmit(handler) {
      $("form").addEventListener("submit", async (event) => {
        event.preventDefault()
        if (busy) return

        showError(null)
        setBusy(true)
        try {
          await handler()
        } catch (error) {
          showError(error.message)
        } finally {
          setBusy(false)
        }
      })
    },

    // Erreur avant que le formulaire n'existe (création de l'intent) : remplace le squelette.
    showError(message) {
      const loading = $("loading")
      if (loading) {
        loading.innerHTML = ""
        loading.appendChild(Object.assign(document.createElement("div"), {
          className: "alert alert-danger small mb-0",
          textContent: message
        }))
        return
      }
      showError(message)
    },

    showSuccess(label) {
      $("loading")?.remove()
      $("form").classList.add("d-none")
      $("done-label").textContent = label
      $("done").classList.remove("d-none")
    },

    get hidden() { return !element.isConnected },
    hide() { busy = false; modal.hide() }
  }
}
